// Third-party
import React from 'react';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { SortingRule } from 'react-table';
import union from 'lodash/union';
import classNames from 'classnames';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { CONTACT_LAYOUT } from '@/utils/contacts';
import { ContactsTableFields } from '../../tables/ContactsTable';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { getMyContacts, GET_CONTACTS, setCurrentContact, setPersistenceSource, GET_POST_MESSAGE_CONTACTS } from '@/redux/modules/contacts';
import { selectIsLoadingSome, startLoaderAction } from '@/redux/modules/ui/loaders';
import { setIsActiveContactPostMessage, setIsActivePortfolioPostMessage } from '@/redux/modules/post-message';

// Types
import { SortingField, SortOrder } from '@/types/data';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import AppMode from '@/types/app-mode';
import UserPreferences from '@/types/user-preferences';
import ThemeMode from '@/types/theme-mode';

// Enums
import { GenericEmptyState } from '../../misc';

// Components
import { SecondaryButton, PrimaryButton } from '../../buttons';
import { ContactsTable } from '../../tables';
import ContactPortfoliosList from '../modal-content/ContactPortfoliosList';
import { CloseButton } from '../../buttons';
import Modal from '@/views/components/modals/AccessibleModal';

type StateProps = {
  user?: User | null;
  contacts: GenericContact[];
  nextContactsToken?: string;
  loading: {
    contacts: boolean;
    portfolios: boolean;
  };
  userPreferences?: UserPreferences;
  isPostMessageActive: boolean;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  startLoaderAction: (name: string) => void;
  setCurrentContact: (contact: GenericContact) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = StateProps &
  DispatchProps & {
    query: string;
    show: boolean;
    selectedContact?: {
      contact: GenericContact;
      portfolios?: Portfolio[];
      hasMore: {
        portfolios: boolean;
      };
      sources?: {
        portfolios?: string | undefined;
      };
    };
    isComparing: boolean;
    loadContactPortfolios: (contact: GenericContact) => Promise<void>;
    loadMoreContactPortfolios: (sort?: SortingField[]) => void;
    onSearchContactPortfolios: (query: string, sort?: SortingField[], shouldFetchNext?: boolean) => void;
    onCancel: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
    resetCurrentContact: () => void;
    layout?: CONTACT_LAYOUT;
  };

type State = {
  selectedPortfolios: Portfolio[];
  contactsSearchQuery: string;
  contactsSortingFields: SortingField[];
  contactPortfoliosSortingFields: SortingField[];
  selectedLinkedContact?: GenericContact;
  initialSelectedContact?: GenericContact;
};

class LegacyContactPortfoliosModal extends React.Component<Props, State> {
  private isModeContact: boolean = !!(this.props.user && this.props.user.appConfig.mode === AppMode.CONTACT);

  private isThemeLight = this.props.userPreferences?.theme === ThemeMode.LIGHT;

  readonly state: State = {
    selectedPortfolios: [],
    contactsSearchQuery: '',
    contactsSortingFields: [],
    contactPortfoliosSortingFields: [],
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    this.props.startLoaderAction(GET_CONTACTS.type);
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, 250);

  componentDidMount() {
    const { selectedContact } = this.props;

    this.setState({ initialSelectedContact: selectedContact?.contact });
    this.props.getMyContacts().then(() => {
      if (this.isModeContact && selectedContact?.contact) {
        this.onSelectContact(selectedContact?.contact);
      }
    });
  }

  clearSearch = () => {
    this.props.onSearchContactPortfolios('');
  };

  clearState = (resetCurrentContact = true) => {
    this.setState({ selectedPortfolios: [] });
    this.clearSearch();
    if (resetCurrentContact || this.isThemeLight) {
      this.props.resetCurrentContact();
    }
  };

  handleBackToContacts = (resetCurrentContact = true) => {
    this.clearState(resetCurrentContact);
    if (this.isModeContact) {
      this.setState({ selectedLinkedContact: undefined });
    }
  };

  handleCancel = () => {
    this.clearState(false);
    this.props.onCancel();

    // For IG we need to make sure the selectedContact stays the same
    if (this.isModeContact && this.state.initialSelectedContact) {
      this.props.setCurrentContact(this.state.initialSelectedContact);
    }
  };

  onSelectPortfolios = (portfolios: Portfolio[]) => {
    const { selectedPortfolios } = this.state;
    const newPortfolios = selectedPortfolios.slice();
    this.setState({ selectedPortfolios: union(newPortfolios, portfolios) });
  };

  onUnselectPortfolios = (portfolios: Portfolio[]) => {
    this.setState(prevState => {
      const { selectedPortfolios } = prevState;
      const newPortfolios = selectedPortfolios.filter(p => !portfolios.includes(p));

      return { selectedPortfolios: newPortfolios };
    });
  };

  onComparePortfoliosCapIntel = (selectedPortfolios: Portfolio[]) => {
    this.props.addPortfoliosToComparison(selectedPortfolios).then(() => {
      if (this.isModeContact && this.state.initialSelectedContact) {
        // Reset the current contact for IG
        this.props.setCurrentContact(this.state.initialSelectedContact);
      }
    });
  };

  onComparePortfolios = () => {
    heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);

    const { selectedPortfolios } = this.state;
    this.onComparePortfoliosCapIntel(selectedPortfolios);
  };

  onSelectContact = (contact: GenericContact) => {
    this.setState({ selectedLinkedContact: contact });
    this.props.loadContactPortfolios(contact);
  };

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { contactsSearchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.debouncedSearchContacts();
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  onLoadMorePortfolios = () => {
    const { contactPortfoliosSortingFields } = this.state;

    this.props.loadMoreContactPortfolios(contactPortfoliosSortingFields);
  };

  onContactPortfoliosSearchChange = (query: string) => {
    const { contactPortfoliosSortingFields } = this.state;
    this.props.onSearchContactPortfolios(query, contactPortfoliosSortingFields, false);
  };

  onContactPortfoliosSortingChange = (sortingRules: SortingRule[]) => {
    const { query } = this.props;
    const contactPortfoliosSortingFields = sortingRules.map(rule => ({
      id: rule.id,
      order: rule.desc ? SortOrder.Desc : SortOrder.Asc,
    }));

    this.setState({ contactPortfoliosSortingFields });
    this.props.onSearchContactPortfolios(query, contactPortfoliosSortingFields, false);
  };

  renderEmptyMessage = () => {
    const { layout } = this.props;
    const emptyStateHeader =
      layout === CONTACT_LAYOUT.CLIENT
        ? formatMessage('EmptyState.NoContactPortfolios.ClientTitle')
        : formatMessage('EmptyState.NoContactPortfolios.ContactTitle');

    return <GenericEmptyState header={emptyStateHeader} />;
  };

  renderNoResultsMessage = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={() => this.props.onSearchContactPortfolios('')}
    />
  );

  renderNoResults = () => {
    const { query } = this.props;

    if (query) {
      return this.renderNoResultsMessage();
    }

    return this.renderEmptyMessage();
  };

  getTableFields = () => {
    const defaultFields = [ContactsTableFields.Email, ContactsTableFields.Category, ContactsTableFields.Id];
    const orderedFields = !this.isThemeLight
      ? [ContactsTableFields.Uid, ContactsTableFields.Fname, ContactsTableFields.Lname]
      : [ContactsTableFields.Fname, ContactsTableFields.Lname, ContactsTableFields.Uid];
    return [...orderedFields, ...defaultFields];
  };

  render() {
    const {
      contacts,
      show,
      user,
      selectedContact,
      query,
      layout,
      isComparing,
      nextContactsToken,
      loading,
      isPostMessageActive,
      setIsActiveContactPostMessage,
      setIsActivePortfolioPostMessage,
      setPersistenceSource,
    } = this.props;

    const { selectedPortfolios, contactsSearchQuery, selectedLinkedContact } = this.state;

    if (!user) {
      return null;
    }

    const title =
      layout === CONTACT_LAYOUT.CLIENT ? formatMessage('ContactPortfoliosModal.SelectClient') : formatMessage('ContactPortfoliosModal.SelectContact');

    const backButtonText = layout === CONTACT_LAYOUT.CLIENT ? 'ContactPortfoliosModal.BackToClients' : 'ContactPortfoliosModal.BackToContacts';

    const emptyStateHeader =
      layout === CONTACT_LAYOUT.CLIENT
        ? formatMessage('EmptyState.NoContactPortfolios.ClientTitle')
        : formatMessage('EmptyState.NoContactPortfolios.ContactTitle');

    const isContacts = !selectedContact || (this.isModeContact && selectedContact && !selectedLinkedContact);
    const isContactDetails = (!this.isModeContact && selectedContact) || (this.isModeContact && selectedContact && selectedLinkedContact);

    return (
      <Modal
        show={show}
        className={classNames('client-portfolios-modal', { light: this.isThemeLight })}
        onHide={this.handleCancel}
        dialogClassName="modal-dialog-centered"
      >
        <Modal.Body className="d-flex flex-column">
          {this.isThemeLight && <CloseButton onClick={this.handleCancel} />}

          <div className="content d-flex flex-column justify-content-center flex-grow-1">
            {isContacts && (
              <ContactsTable
                selectable={false}
                className="flex-fill"
                contacts={contacts}
                title={title}
                onClick={this.onSelectContact}
                user={user}
                layout={layout}
                onSortingChange={this.onContactsSortingChange}
                onLoadMore={this.onLoadMoreContacts}
                hasMore={nextContactsToken != null}
                onContactsSearch={this.onContactsSearchChange}
                debouncedSearchContacts={this.debouncedSearchContacts}
                searchQuery={contactsSearchQuery}
                loading={loading.contacts}
                isThemeLight={this.isThemeLight}
                orderedFields={this.getTableFields()}
                isPostMessageActive={isPostMessageActive}
                setPersistenceSource={setPersistenceSource}
                setIsActiveContactPostMessage={setIsActiveContactPostMessage}
              />
            )}

            {isContactDetails && (
              <ContactPortfoliosList
                user={user}
                backButtonText={backButtonText}
                emptyStateHeader={emptyStateHeader}
                isLoading={loading.portfolios}
                searchQuery={query}
                selectedContact={selectedContact}
                onBack={this.handleBackToContacts}
                onLoadMorePortfolios={this.onLoadMorePortfolios}
                onSearchPortfolios={this.onContactPortfoliosSearchChange}
                onSelectPortfolios={this.onSelectPortfolios}
                onSortPortfolios={this.onContactPortfoliosSortingChange}
                onUnselectPortfolios={this.onUnselectPortfolios}
                isThemeLight={this.isThemeLight}
                isPostMessageActive={isPostMessageActive}
                setPersistenceSource={setPersistenceSource}
                setIsActivePortfolioPostMessage={setIsActivePortfolioPostMessage}
              />
            )}
          </div>

          {((isContacts && !this.isThemeLight) || isContactDetails) && (
            <div className="modal-actions d-flex justify-content-end">
              <PrimaryButton loading={isComparing} disabled={!selectedPortfolios.length} className="compare-button" onClick={this.onComparePortfolios}>
                <FormattedMessage
                  id={`ContactPortfoliosModal.${isComparing ? 'ComparingSelected' : !this.isThemeLight ? 'CompareSelected' : 'CompareSelection'}`}
                  values={{ count: selectedPortfolios.length }}
                />
              </PrimaryButton>

              <SecondaryButton className="cancel-button" onClick={this.handleCancel} disabled={isComparing}>
                <FormattedMessage id="Common.Close" />
              </SecondaryButton>
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user,
  contacts: state.contacts.contacts,
  nextContactsToken: state.contacts.nextContactsToken,
  loading: {
    contacts: selectIsLoadingSome(state, [GET_CONTACTS.type, GET_POST_MESSAGE_CONTACTS.type]),
    portfolios: state.contacts.loading.current.portfolios,
  },
  userPreferences: state.auth.user?.preferences,
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default connect(mapStateToProps, {
  getMyContacts,
  startLoaderAction,
  setCurrentContact,
  setIsActiveContactPostMessage,
  setIsActivePortfolioPostMessage,
  setPersistenceSource,
})(LegacyContactPortfoliosModal);
